import {
    getUserDeviceTokens,
    updateUserLocation,
} from "../services/userService"
import {
    getCollectiveUser,
    updateCollectiveUser,
} from "../services/firebaseConnector"
import { getLocationPlace } from "../services/placesConnector"
import { consoleLog, buildFullName } from "./helpers"
import { LOCATION_STALE_THRESHOLD_MS } from "../const/numbers"
import {
    LOCALSTORAGE_LAST_WEB_LOCATION_SYNC,
    LOCALSTORAGE_RESOLVED_PLACE,
} from "../const/storage"
import { readResolvedPlace, toCachedPlace } from "./resolvedPlaceCache"

// Same default the Android app uses when a resolved place has no score yet.
const DEFAULT_PERSONAL_SCORE = 15

/**
 * The last resolution's place when `coordinates` are still within its
 * reuse radius and it is fresh; otherwise null.
 */
export const readCachedResolvedPlace = (coordinates) => {
    let cached = null
    try {
        cached = JSON.parse(
            localStorage.getItem(LOCALSTORAGE_RESOLVED_PLACE) || "null",
        )
    } catch {
        cached = null
    }
    const { reuse, place } = readResolvedPlace(cached, coordinates)
    return reuse ? place : null
}

/** Records a resolution (a null place too, as a brief failure marker). */
export const rememberResolvedPlace = (coordinates, place) => {
    localStorage.setItem(
        LOCALSTORAGE_RESOLVED_PLACE,
        JSON.stringify({
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
            resolvedAt: Date.now(),
            place: toCachedPlace(place),
        }),
    )
}

/**
 * Resolves the compendium place for the given coordinates, reusing the last
 * resolution while the user has not moved (about 1 km) and it is fresh.
 * getLocationPlace is the expensive part: a Google Geocoding request plus
 * Places Autocomplete and Place Details, so a stationary web user pays for it
 * once, not on every page load and every poll. Only our own entity fields are
 * cached, never Google place details.
 */
const resolvePlace = async (coordinates) => {
    let cached = null
    try {
        cached = JSON.parse(
            localStorage.getItem(LOCALSTORAGE_RESOLVED_PLACE) || "null",
        )
    } catch {
        cached = null
    }

    const { reuse, place } = readResolvedPlace(cached, coordinates)
    if (reuse) {
        consoleLog("Reusing the last resolved place; skipping Google calls.")
        return place
    }

    let resolved = null
    try {
        resolved = await getLocationPlace({ coordinates })
    } catch (placeError) {
        consoleLog("Could not resolve place for location sync:", placeError)
    }

    // A failure is cached too (briefly), so reloads do not retry the chain.
    rememberResolvedPlace(coordinates, resolved)
    return toCachedPlace(resolved)
}

/**
 * True when the collective entry holds a recent location written by someone
 * other than this browser. Android's merge-style writes never clear a marker
 * left by the web, so ownership is judged by comparing the entry's
 * last_refresh with the exact timestamp this browser last wrote.
 */
const hasFreshLocationFromOtherWriter = (entry) => {
    const lastRefresh = entry?.last_refresh
    if (!lastRefresh) return false

    if (lastRefresh === localStorage.getItem(LOCALSTORAGE_LAST_WEB_LOCATION_SYNC)) {
        return false
    }

    const ageMs = Date.now() - new Date(lastRefresh).getTime()
    return Number.isFinite(ageMs) && ageMs < LOCATION_STALE_THRESHOLD_MS
}

/**
 * Publishes the signed-in user's browser-detected location, for users whose
 * only location source is the web app. Mirrors what the Android app writes
 * (coordinates, resolved place and personal_score) without ever overwriting
 * data supplied by the mobile app:
 *
 * 1. A registered device token means the mobile app owns this user's
 *    location, so nothing is written.
 * 2. Otherwise, a fresh location from another writer in the Firebase
 *    collective also blocks the write.
 * 3. Failing to fetch device tokens blocks the write rather than risk
 *    clobbering; an unreadable collective entry falls back to check 1 alone.
 *
 * CloudSQL is written first (authoritative); the Firebase collective is the
 * backup, and is still written if CloudSQL fails. The resolved place and
 * personal_score only exist in the collective for now, since
 * PATCH /v1/user/:id/location persists coordinates only.
 * @param {{latitude: number, longitude: number}} coordinates
 * @returns {Promise<void>}
 */
export const syncUserLocation = async (coordinates) => {
    try {
        const userData = localStorage.getItem("chaperon_user_data")
        const authToken = localStorage.getItem("chaperon_auth_token")

        if (!userData || !authToken) {
            return
        }

        const user = JSON.parse(userData)
        if (!user?.id || !user?.username) {
            return
        }

        const deviceTokens = await getUserDeviceTokens()
        if ((deviceTokens?.data?.length ?? 0) > 0) {
            consoleLog(
                `Skipping location sync for ${user.username}: user has a registered device token.`,
            )
            return
        }

        // If the entry can't be read (e.g. database rules deny reads), the
        // device-token check above remains the only guard; warn so a missing
        // read permission is visible rather than silent.
        let existingEntry = null
        try {
            existingEntry = await getCollectiveUser(user.username)
        } catch (readError) {
            console.warn(
                "Could not read collective entry for the other-writer check:",
                readError,
            )
        }
        if (hasFreshLocationFromOtherWriter(existingEntry)) {
            consoleLog(
                `Skipping location sync for ${user.username}: a fresher location from another client exists.`,
            )
            return
        }

        const place = await resolvePlace(coordinates)

        try {
            await updateUserLocation(
                user.id,
                coordinates.latitude,
                coordinates.longitude,
                place?.id,
            )
        } catch (sqlError) {
            console.error(
                "Failed to persist location to CloudSQL; writing Firebase backup anyway:",
                sqlError,
            )
        }

        // update() treats null as "delete this field", so entity fields are
        // only included when a place actually resolved.
        const lastRefresh = new Date().toISOString()
        const updates = {
            member_id: String(user.id),
            username: user.username,
            name: buildFullName(user.first_name, user.last_name) || user.username,
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
            last_refresh: lastRefresh,
        }

        if (place?.id != null && place?.name && place?.slug) {
            updates.entity_id = place.id
            updates.entity_name = place.name
            updates.country = place.country ?? ""
            updates.entity_slug = place.slug
            updates.personal_score =
                place.overall_safety ?? DEFAULT_PERSONAL_SCORE
            if (place.country_code) {
                updates.country_code = place.country_code
            }
        }

        await updateCollectiveUser(user.username, updates)
        localStorage.setItem(LOCALSTORAGE_LAST_WEB_LOCATION_SYNC, lastRefresh)
        consoleLog(`Synced location for ${user.username}.`)
    } catch (error) {
        console.error("Error syncing user location:", error)
    }
}
